import type { SceneNode } from '@/lib/scene-document';

/**
 * Hook for all editor pointer and keyboard event handling.
 * Translates DOM events into Zustand store actions.
 */
import { useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

import { computeLayout } from './canvaskit-layout';
import { generateNodeId } from './canvaskit-mutations';
import { screenToScene as _screenToScene } from './coordinate-utils';
import { type DropPlacement, readClipboardImageFiles } from './canvas-dnd';
import { hitTestEdge, hitTestVectorPoint } from './canvaskit-vector-points';
import { parseCanvasClipboard, serializeCanvasClipboard } from './canvas-clipboard';
import { hitTestCropHandle, resolveCropTarget, getCursorForCropHandle } from './canvaskit-crop';
import { hitTest, type Point, getSvgPathBounds, getSelectionAABB, getNodeWorldBounds, getParentWorldOffset } from './canvaskit-hit-test';
import {
  hitTestHandle,
  type HandleId,
  nodeCornerRadii,
  getEnabledHandles,
  getHandlePositions,
  getCursorForHandle,
  hitTestRadiusHandle,
  supportsRadiusHandles,
  shouldShowRadiusHandles,
  getRadiusHandlePositions,
} from './canvaskit-transform';

import type { AlignEdge } from './canvaskit-align';
import type { DrawToolType, CanvasEditorStore } from './canvaskit-editor-store';

// The 8 resize handles (no rotate) drawn on a 2+-node selection box.
const MULTI_RESIZE_HANDLES: HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// ── Copy/paste style (Cmd/Ctrl+Opt+C / V) ──────────────────────────────────

// Visual style keys copied for every node type.
const STYLE_VISUAL_KEYS = [
  'fills',
  'strokes',
  'strokeWeight',
  'strokeAlign',
  'strokeJoin',
  'strokeCap',
  'dashPattern',
  'effects',
  'opacity',
  'blendMode',
] as const;
// Corner radius only lands on node types that actually render it.
const STYLE_RADIUS_KEYS = ['cornerRadius', 'cornerRadii'] as const;
const STYLE_RADIUS_TYPES = new Set(['RECTANGLE', 'IMAGE', 'FRAME', 'GROUP']);
// Typographic keys copied only between TEXT nodes.
const STYLE_TEXT_KEYS = [
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'textAlign',
  'lineHeight',
  'letterSpacing',
  'textDecoration',
  'textTransform',
] as const;

// Module-level so a copied style survives across selections, like Figma.
let styleClipboard: Partial<SceneNode> | null = null;

/**
 * Mirror the selection just placed in the store's clipboard onto the SYSTEM
 * clipboard, so another canvas — another tab, another window — can paste it.
 *
 * Fire-and-forget by design: the in-editor clipboard is already set, so a
 * refused write (insecure context, denied permission) costs only cross-document
 * paste, which the paste handler falls back from. `writeText` needs no
 * permission prompt when called from a user gesture, which a Cmd+C keydown is.
 */
function writeSystemClipboard(store: CanvasEditorStore): void {
  const s = store.getState();
  if (!s.clipboardNodes || s.clipboardTopIds.length === 0 || !s.clipboardStamp) return;

  const text = serializeCanvasClipboard({
    nodes: s.clipboardNodes,
    topIds: s.clipboardTopIds,
    stamp: s.clipboardStamp,
    brand: s.brandContext,
    width: s.doc.width,
    height: s.doc.height,
  });
  if (!text) {
    // Over the size cap. Same-canvas paste still works off the in-editor
    // clipboard, so say what was lost rather than failing silently.
    toast.warning('Selection is too large to paste into another canvas');
    return;
  }

  void navigator.clipboard?.writeText(text).catch((err: unknown) => {
    console.warn('[canvas] system clipboard write refused', err);
  });
}

function cloneStyleValue(v: unknown): unknown {
  if (v == null || typeof v !== 'object') return v;
  return structuredClone(v);
}

/** Snapshot a node's visual (and, for TEXT, typographic) style. */
function snapshotNodeStyle(node: SceneNode): Partial<SceneNode> {
  const snap: Record<string, unknown> = {};
  for (const k of STYLE_VISUAL_KEYS) snap[k] = node[k];
  for (const k of STYLE_RADIUS_KEYS) snap[k] = node[k];
  if (node.type === 'TEXT') {
    for (const k of STYLE_TEXT_KEYS) snap[k] = node[k];
  }
  return snap as Partial<SceneNode>;
}

/**
 * Build the style patch to apply to one paste target: skip properties the
 * target's node type can't carry (corner radius on text, typography on shapes)
 * and deep-clone arrays/objects so pasted nodes never share references.
 */
function styleForTarget(snap: Partial<SceneNode>, target: SceneNode): Partial<SceneNode> {
  const raw = snap as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of STYLE_VISUAL_KEYS) {
    if (k in raw) out[k] = cloneStyleValue(raw[k]);
  }
  if (STYLE_RADIUS_TYPES.has(target.type)) {
    for (const k of STYLE_RADIUS_KEYS) {
      if (k in raw) out[k] = cloneStyleValue(raw[k]);
    }
  }
  if (target.type === 'TEXT') {
    for (const k of STYLE_TEXT_KEYS) {
      if (k in raw) out[k] = cloneStyleValue(raw[k]);
    }
  }
  return out as Partial<SceneNode>;
}

export interface EditorEventOptions {
  /**
   * Called on double-click of a selected node, before default group-enter logic.
   * Return `true` to prevent the default behavior (group enter / text edit).
   */
  onDoubleClickNode?: (nodeId: string, node: SceneNode) => boolean;

  /**
   * Called on pointer-up when a node was clicked (not dragged).
   * Fires after selection is applied. Return `true` to consume the event.
   */
  onClickNode?: (nodeId: string, node: SceneNode, meta: { shiftKey: boolean }) => boolean;

  /**
   * Called during drag moves with the current scene-space position and
   * the set of node IDs being dragged. Used by the workspace to detect
   * when a drag crosses a frame boundary for cross-frame DnD.
   */
  onDragMove?: (scenePoint: Point, draggedIds: Set<string>) => void;

  /**
   * When true, scroll-wheel zoom and pan are disabled. Use for embedded
   * canvas sections where the outer viewport handles navigation.
   */
  lockViewport?: boolean;

  /**
   * Called when images are pasted onto the canvas (a screenshot, a copied image
   * file). The owner uploads them and places the result, exactly as it does for
   * an OS file drop. Without this, pasted images are ignored.
   */
  onOsFilesDropped?: (files: File[], placement: DropPlacement) => void;
}

export function useEditorEvents(
  store: CanvasEditorStore,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  options?: EditorEventOptions,
) {
  const spaceHeldRef = useRef(false);
  const mouseDownRef = useRef(false);
  const pointerDownScreenRef = useRef<{ x: number; y: number } | null>(null);
  const clickedNodeRef = useRef<string | null>(null);
  const moveRafRef = useRef<number>(0);
  // Alt+drag duplicate is deferred until the pointer actually moves, so a plain
  // Alt+click just selects (no stray in-place copy). Holds the scene-space
  // origin captured at pointer-down until the move threshold is crossed.
  const altDragPendingRef = useRef<Point | null>(null);
  // Last pointer position in screen coords, for paste-at-cursor.
  const pointerClientRef = useRef<{ x: number; y: number } | null>(null);
  // Opacity number-key entry: remembers the first digit so a second digit typed
  // within the compose window builds a two-digit value (5 then 5 = 55%).
  const opacityTypingRef = useRef<{ digit: string; at: number } | null>(null);

  // ── Coordinate conversion ────────────────────────────────────────

  const screenToScene = useCallback(
    (screenX: number, screenY: number): Point => {
      const state = store.getState();
      const canvasEl = canvasRef.current;
      if (!canvasEl) return { x: 0, y: 0 };
      return _screenToScene(screenX, screenY, canvasEl, state.zoom, state.panOffset);
    },
    [store, canvasRef],
  );

  // ── Pointer events ───────────────────────────────────────────────

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const state = store.getState();
      const scenePoint = screenToScene(e.clientX, e.clientY);
      mouseDownRef.current = true;
      pointerDownScreenRef.current = { x: e.clientX, y: e.clientY };
      clickedNodeRef.current = null;

      // Any pointer interaction dismisses the Alt-hover measurement overlay.
      if (state.altMeasureActive) {
        store.setState({ altMeasureActive: false, renderVersion: state.renderVersion + 1 });
      }

      // If editing text, commit on click outside the text node
      if (state.editingTextId) {
        // The contenteditable overlay handles its own clicks;
        // this fires when clicking back on the canvas
        return;
      }

      // Crop mode: resize handles, drag to reposition, click outside to exit
      if (state.croppingNodeId) {
        const cropNode = state.doc.nodes[state.croppingNodeId];
        if (cropNode) {
          // Resolve crop node to scene-space coords (node.x/y are local to parent).
          const cropParent = getParentWorldOffset(state.doc, state.croppingNodeId);
          const cropWorldX = cropParent.x + cropNode.x;
          const cropWorldY = cropParent.y + cropNode.y;

          // Check if click hits a crop resize handle first
          const cropHandleHit = hitTestCropHandle(
            scenePoint,
            cropWorldX,
            cropWorldY,
            cropNode.width,
            cropNode.height,
            state.zoom,
          );
          if (cropHandleHit) {
            state.startCropResize(cropHandleHit, scenePoint);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            return;
          }

          // Check if click is within the frame bounds (start dragging)
          const inFrame =
            scenePoint.x >= cropWorldX &&
            scenePoint.x <= cropWorldX + cropNode.width &&
            scenePoint.y >= cropWorldY &&
            scenePoint.y <= cropWorldY + cropNode.height;

          if (inFrame) {
            state.startCropDrag(scenePoint);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            return;
          }
        }
        // Click outside frame: exit crop mode
        state.exitCropMode();
        return;
      }

      // Vector edit mode: hit-test points, or exit
      if (state.vectorEditingNodeId && state.vectorPaths) {
        const node = state.doc.nodes[state.vectorEditingNodeId];
        if (node?.pathData) {
          const pathBounds = getSvgPathBounds(node.pathData);
          // Convert scene point to node-local space (accounting for parent offset)
          const parentOff = getParentWorldOffset(state.doc, state.vectorEditingNodeId);
          const localX = scenePoint.x - (parentOff.x + node.x);
          const localY = scenePoint.y - (parentOff.y + node.y);
          const localPoint = { x: localX, y: localY };

          const hit = hitTestVectorPoint(
            localPoint,
            state.vectorPaths,
            pathBounds,
            node.width,
            node.height,
            state.zoom,
          );

          if (hit) {
            if (e.shiftKey) {
              state.toggleVectorPoint(hit.pathIndex, hit.pointIndex);
            } else {
              state.selectVectorPoint(hit.pathIndex, hit.pointIndex);
            }
            if (hit.part === 'anchor' || hit.part === 'handleIn' || hit.part === 'handleOut') {
              state.startVectorDrag(hit, localPoint);
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
            }
            return;
          }

          // Check if click is still within the node bounds
          const inNode =
            localX >= 0 && localX <= node.width &&
            localY >= 0 && localY <= node.height;

          if (!inNode) {
            state.exitVectorEditMode();
            // Fall through to normal selection
          } else {
            // No point hit -- check if clicking on an edge to add a point
            const edgeHit = hitTestEdge(
              localPoint,
              state.vectorPaths,
              pathBounds,
              node.width,
              node.height,
              state.zoom,
            );
            if (edgeHit) {
              state.addPointOnEdge(edgeHit);
              return;
            }
            // Clicked inside node but not on a point or edge -- deselect points
            store.setState({
              vectorSelectedPoints: new Set(),
              renderVersion: store.getState().renderVersion + 1,
            });
            return;
          }
        }
      }

      // Pen tool mode: click places points
      if (state.drawTool === 'PEN' && !spaceHeldRef.current && e.button === 0) {
        state.penToolClick(scenePoint);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }

      // Freehand tool mode: start drawing
      if (state.drawTool === 'FREEHAND' && !spaceHeldRef.current && e.button === 0) {
        state.freehandStart(scenePoint);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }

      // Draw tool mode: start drawing a shape
      if (state.drawTool && state.drawTool !== 'PEN' && state.drawTool !== 'FREEHAND' && !spaceHeldRef.current && e.button === 0) {
        state.startDrawing(scenePoint);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }

      // Pan mode — disabled when viewport is locked (e.g. embedded canvas sections)
      if ((spaceHeldRef.current || e.button === 1) && !options?.lockViewport) {
        state.startPan({ x: e.clientX, y: e.clientY });
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }

      if (e.button === 2) return;

      // Handle hit (single selection only)
      if (state.selectedIds.size === 1) {
        const nodeId = [...state.selectedIds][0];
        const selectedNode = state.doc.nodes[nodeId];
        // Skip resize/rotate handles for email-locked frames
        const isEmailLocked = selectedNode?.type === 'FRAME' && (selectedNode?.emailLocked === true || selectedNode?.pluginData?.['emailLocked'] === true);
        if (!isEmailLocked) {
          // Read bounds from the laid-out doc so the handle hit zone matches
          // where the renderer painted it (auto-layout shifts child x/y).
          const bounds = getNodeWorldBounds(computeLayout(state.doc), nodeId);
          const handles = getHandlePositions(bounds);
          const handleHit = hitTestHandle(
            scenePoint,
            handles,
            state.zoom,
            getEnabledHandles(selectedNode),
          );

          if (handleHit === 'rotate') {
            state.startRotate(scenePoint);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            return;
          }
          if (handleHit) {
            state.startResize(handleHit, scenePoint);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            return;
          }

          // Corner-radius circles (inset on the corner diagonals; resize
          // squares get priority since they share the corner region).
          if (supportsRadiusHandles(selectedNode) && shouldShowRadiusHandles(bounds, state.zoom)) {
            const radiusHit = hitTestRadiusHandle(
              scenePoint,
              getRadiusHandlePositions(bounds, nodeCornerRadii(selectedNode), state.zoom),
              state.zoom,
            );
            if (radiusHit) {
              state.startRadiusDrag(radiusHit, scenePoint);
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              return;
            }
          }
        }
      }

      // Handle hit for a multi-selection: the combined bounding box exposes the
      // 8 resize handles (no rotate/radius). Test them before scene selection so
      // grabbing a corner scales the whole group.
      if (state.selectedIds.size > 1) {
        const aabb = getSelectionAABB(computeLayout(state.doc), state.selectedIds);
        if (aabb) {
          const handles = getHandlePositions({ ...aabb, rotation: 0 });
          const handleHit = hitTestHandle(scenePoint, handles, state.zoom, MULTI_RESIZE_HANDLES);
          if (handleHit && handleHit !== 'rotate') {
            state.startResize(handleHit, scenePoint);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            return;
          }
        }
      }

      // Scene hit test -- in link-zone mode, only zones are hittable.
      // Cmd/Ctrl+click deep-selects: bypass group traversal and grab the
      // deepest node under the cursor (the same deep hit double-click descends
      // into). Shift+Cmd+click toggles that deep node in/out of selection.
      const isDeepSelect =
        (e.metaKey || e.ctrlKey) && !e.altKey && !state.linkZonesVisible;
      let hit = hitTest(state.doc, scenePoint, state.enteredGroupId, state.linkZonesVisible);
      if (isDeepSelect) {
        const deep = hitTest(state.doc, scenePoint, null);
        hit = deep ? { ...deep, selectableId: deep.hitId } : null;
      }

      if (hit) {
        const hitNode = state.doc.nodes[hit.selectableId];
        const hitIsEmailLocked = hitNode?.type === 'FRAME' && (hitNode?.emailLocked === true || hitNode?.pluginData?.['emailLocked'] === true);

        // Alt+drag duplicate: select the target, then defer the clone until the
        // pointer actually moves (see handlePointerMove). A bare Alt+click just
        // selects. Not for email-locked frames (they don't drag).
        if (e.altKey && !e.shiftKey && !hitIsEmailLocked) {
          if (!state.selectedIds.has(hit.selectableId)) {
            state.select(hit.selectableId);
          }
          altDragPendingRef.current = scenePoint;
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          return;
        }

        if (e.shiftKey) {
          state.toggleSelect(hit.selectableId);
        } else {
          if (!state.selectedIds.has(hit.selectableId)) {
            state.select(hit.selectableId);
          }
          clickedNodeRef.current = hit.selectableId;
        }
        // Email-locked frames: allow selection but not dragging
        if (!hitIsEmailLocked) {
          state.startDrag(scenePoint);
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
        }
      } else {
        if (!e.shiftKey) {
          state.clearSelection();
        }
        state.startMarquee(scenePoint);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }
    },
    [store, screenToScene],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      let state = store.getState();
      const scenePoint = screenToScene(e.clientX, e.clientY);
      pointerClientRef.current = { x: e.clientX, y: e.clientY };

      // Deferred Alt+drag duplicate: once the pointer clears the click
      // threshold, clone the selection in place and switch into a normal drag
      // of the copies. This same event then falls through to the drag branch.
      if (altDragPendingRef.current && mouseDownRef.current) {
        const down = pointerDownScreenRef.current;
        const dxs = e.clientX - (down?.x ?? e.clientX);
        const dys = e.clientY - (down?.y ?? e.clientY);
        if (dxs * dxs + dys * dys < 25) return; // < 5px: not a drag yet
        const origin = altDragPendingRef.current;
        altDragPendingRef.current = null;
        state.beginAltDragDuplicate(origin);
        state = store.getState();
      }

      switch (state.interactionMode) {
        case 'panning':
          state.updatePan({ x: e.clientX, y: e.clientY });
          return;
        case 'dragging':
        case 'resizing':
        case 'rotating':
        case 'radiusDragging':
        case 'marquee':
        case 'cropping':
        case 'cropResizing':
        case 'drawing':
        case 'vectorDragging': {
          if (moveRafRef.current) cancelAnimationFrame(moveRafRef.current);
          const mode = state.interactionMode;
          const shiftKey = e.shiftKey;
          const altKey = e.altKey;
          // Snapping is now suppressed with Cmd/Ctrl (Alt duplicates instead).
          const disableSnap = e.metaKey || e.ctrlKey;
          const canvasEl = canvasRef.current;
          moveRafRef.current = requestAnimationFrame(() => {
            moveRafRef.current = 0;
            const s = store.getState();
            switch (mode) {
              case 'dragging':
                s.updateDrag(scenePoint, shiftKey, disableSnap);
                options?.onDragMove?.(scenePoint, s.selectedIds);
                break;
              case 'resizing':
                s.updateResize(scenePoint, shiftKey, altKey);
                break;
              case 'rotating':
                s.updateRotate(scenePoint, shiftKey);
                break;
              case 'radiusDragging':
                s.updateRadiusDrag(scenePoint, altKey);
                break;
              case 'marquee':
                s.updateMarquee(scenePoint);
                break;
              case 'cropping':
                s.updateCropDrag(scenePoint);
                if (canvasEl) canvasEl.style.cursor = 'grabbing';
                break;
              case 'cropResizing':
                s.updateCropResize(scenePoint);
                break;
              case 'drawing':
                s.updateDrawing(scenePoint, shiftKey);
                break;
              case 'vectorDragging': {
                const vecNode = s.vectorEditingNodeId ? s.doc.nodes[s.vectorEditingNodeId] : null;
                if (vecNode) {
                  const po = getParentWorldOffset(s.doc, s.vectorEditingNodeId!);
                  s.updateVectorDrag({ x: scenePoint.x - (po.x + vecNode.x), y: scenePoint.y - (po.y + vecNode.y) });
                }
                break;
              }
            }
          });
          return;
        }
      }

      // Crop mode idle: show resize cursor for handles, grab for image pan
      if (state.croppingNodeId && !mouseDownRef.current) {
        const cropNode = state.doc.nodes[state.croppingNodeId];
        if (cropNode && canvasRef.current) {
          const cropParent = getParentWorldOffset(state.doc, state.croppingNodeId);
          const cropWorldX = cropParent.x + cropNode.x;
          const cropWorldY = cropParent.y + cropNode.y;

          // Check crop handle hover first
          const cropHandleHover = hitTestCropHandle(
            scenePoint,
            cropWorldX,
            cropWorldY,
            cropNode.width,
            cropNode.height,
            state.zoom,
          );
          if (cropHandleHover) {
            canvasRef.current.style.cursor = getCursorForCropHandle(cropHandleHover);
            return;
          }

          const inFrame =
            scenePoint.x >= cropWorldX &&
            scenePoint.x <= cropWorldX + cropNode.width &&
            scenePoint.y >= cropWorldY &&
            scenePoint.y <= cropWorldY + cropNode.height;
          canvasRef.current.style.cursor = inFrame ? 'grab' : 'default';
        }
        return;
      }

      // Freehand tool: collect points while drawing
      if (state.drawTool === 'FREEHAND' && state.freehandPoints && mouseDownRef.current) {
        state.freehandMove(scenePoint);
        return;
      }

      // Pen tool: drag creates handles on the last point
      if (state.drawTool === 'PEN' && state.penCurrentPath && mouseDownRef.current) {
        state.penToolDrag(scenePoint);
        return;
      }

      // Vector edit mode: show crosshair when over node
      if (state.vectorEditingNodeId && canvasRef.current) {
        const vecNode = state.doc.nodes[state.vectorEditingNodeId];
        if (vecNode) {
          const localX = scenePoint.x - vecNode.x;
          const localY = scenePoint.y - vecNode.y;
          const inNode = localX >= 0 && localX <= vecNode.width && localY >= 0 && localY <= vecNode.height;
          canvasRef.current.style.cursor = inNode ? 'crosshair' : 'default';
        }
        return;
      }

      // Draw tool active: show crosshair
      if (state.drawTool && canvasRef.current) {
        canvasRef.current.style.cursor = 'crosshair';
        return;
      }

      // Idle: hover + cursor
      if (!mouseDownRef.current) {
        if (state.selectedIds.size === 1) {
          const nodeId = [...state.selectedIds][0];
          // Match the laid-out coords used by the renderer (and pointerdown).
          const bounds = getNodeWorldBounds(computeLayout(state.doc), nodeId);
          const handles = getHandlePositions(bounds);
          const handleHit = hitTestHandle(
            scenePoint,
            handles,
            state.zoom,
            getEnabledHandles(state.doc.nodes[nodeId]),
          );

          if (handleHit && canvasRef.current) {
            canvasRef.current.style.cursor = getCursorForHandle(handleHit, bounds.rotation);
            state.setHovered(null);
            return;
          }

          const node = state.doc.nodes[nodeId];
          if (
            canvasRef.current &&
            supportsRadiusHandles(node) &&
            shouldShowRadiusHandles(bounds, state.zoom) &&
            hitTestRadiusHandle(
              scenePoint,
              getRadiusHandlePositions(bounds, nodeCornerRadii(node), state.zoom),
              state.zoom,
            )
          ) {
            canvasRef.current.style.cursor = 'pointer';
            state.setHovered(null);
            return;
          }
        }

        // Multi-selection: show the resize cursor over the group's handles.
        // When not over a handle this falls through to normal hover so the
        // Alt-measurement overlay can still pick up the hovered target node.
        if (state.selectedIds.size > 1 && canvasRef.current) {
          const aabb = getSelectionAABB(computeLayout(state.doc), state.selectedIds);
          if (aabb) {
            const handles = getHandlePositions({ ...aabb, rotation: 0 });
            const handleHit = hitTestHandle(scenePoint, handles, state.zoom, MULTI_RESIZE_HANDLES);
            if (handleHit && handleHit !== 'rotate') {
              canvasRef.current.style.cursor = getCursorForHandle(handleHit, 0);
              state.setHovered(null);
              return;
            }
          }
        }

        const hit = hitTest(state.doc, scenePoint, state.enteredGroupId, state.linkZonesVisible);
        if (canvasRef.current) {
          // Show a "grab" cursor over a reorderable section (one whose parent
          // is an auto-layout stack) so it reads as draggable, like Figma; a
          // plain "move" cursor for free-positioned nodes.
          let cursor: string = 'default';
          if (spaceHeldRef.current) {
            cursor = 'grab';
          } else if (hit) {
            const sel = state.doc.nodes[hit.selectableId];
            const parent = sel?.parentId ? state.doc.nodes[sel.parentId] : null;
            const reorderable =
              parent?.layoutMode === 'VERTICAL' || parent?.layoutMode === 'HORIZONTAL';
            cursor = reorderable ? 'grab' : 'move';
          }
          canvasRef.current.style.cursor = cursor;
        }
        state.setHovered(hit?.selectableId ?? null);
      }
    },
    [store, screenToScene, canvasRef],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (moveRafRef.current) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = 0;
      }
      const state = store.getState();
      mouseDownRef.current = false;
      // A bare Alt+click (no drag) never duplicated — just drop the pending gesture.
      altDragPendingRef.current = null;

      // Freehand tool end
      if (state.drawTool === 'FREEHAND' && state.freehandPoints) {
        state.freehandEnd();
        return;
      }

      // Pen tool drag end
      if (state.drawTool === 'PEN' && state.penDragging) {
        state.penToolEndDrag();
      }

      switch (state.interactionMode) {
        case 'panning': state.endPan(); break;
        case 'dragging': state.endDrag(); break;
        case 'resizing': state.endResize(); break;
        case 'rotating': state.endRotate(); break;
        case 'radiusDragging': state.endRadiusDrag(); break;
        case 'marquee': state.endMarquee(); break;
        case 'cropping': state.endCropDrag(); break;
        case 'cropResizing': state.endCropResize(); break;
        case 'drawing': state.endDrawing(); break;
        case 'vectorDragging': state.endVectorDrag(); break;
      }

      // Fire onClickNode if pointer barely moved (click, not drag)
      const clickedId = clickedNodeRef.current;
      const downPos = pointerDownScreenRef.current;
      clickedNodeRef.current = null;
      pointerDownScreenRef.current = null;

      if (clickedId && downPos && options?.onClickNode) {
        const dx = e.clientX - downPos.x;
        const dy = e.clientY - downPos.y;
        if (dx * dx + dy * dy < 25) { // 5px threshold
          const node = state.doc.nodes[clickedId];
          if (node) {
            options.onClickNode(clickedId, node, { shiftKey: e.shiftKey });
          }
        }
      }
    },
    [store, options],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const state = store.getState();
      const scenePoint = screenToScene(e.clientX, e.clientY);

      // If nothing selected, try to hit-test under the cursor for the double-click target
      let targetNodeId: string | undefined;
      let targetNode: SceneNode | undefined;

      if (state.selectedIds.size === 1) {
        targetNodeId = [...state.selectedIds][0];
        targetNode = state.doc.nodes[targetNodeId] as SceneNode | undefined;
      } else {
        // Fallback: hit-test to find what's under the cursor
        const hit = hitTest(state.doc, scenePoint, state.enteredGroupId, state.linkZonesVisible);
        if (hit) {
          targetNodeId = hit.selectableId;
          targetNode = state.doc.nodes[targetNodeId] as SceneNode | undefined;
          // Also select it
          state.select(targetNodeId);
        }
      }

      if (targetNodeId && targetNode) {
        // Allow workspace to intercept double-click (e.g., for entering workspace frames)
        if (options?.onDoubleClickNode?.(targetNodeId, targetNode)) {
          return;
        }

        if (targetNode.type === 'TEXT') {
          state.startTextEdit(targetNodeId);
          return;
        }

        if (targetNode.type === 'IMAGE') {
          state.enterCropMode(targetNodeId);
          return;
        }

        // A shape holding a photo (IMAGE fill — circle crop, blob-masked
        // image): double-click edits the crop, same as an IMAGE node. Vector
        // editing stays reachable from the properties panel.
        if (
          ['PATH', 'RECTANGLE', 'ELLIPSE', 'POLYGON', 'STAR', 'TRAPEZOID', 'SEAL'].includes(targetNode.type) &&
          resolveCropTarget(targetNode)
        ) {
          state.enterCropMode(targetNodeId);
          // enterCropMode declines while the image bytes are still loading
          // (no cached SkImage) — fall through to vector editing instead of
          // leaving the double-click dead.
          if (store.getState().croppingNodeId === targetNodeId) return;
        }

        if (['PATH', 'RECTANGLE', 'ELLIPSE', 'POLYGON', 'STAR', 'TRAPEZOID', 'SEAL'].includes(targetNode.type)) {
          if (targetNode.type !== 'PATH' || targetNode.pathData) {
            state.enterVectorEditMode(targetNodeId);
            return;
          }
        }

        if (targetNode.type === 'GROUP' || targetNode.type === 'FRAME') {
          // Root-level hit test finds the deepest node under the cursor
          const rootHit = hitTest(state.doc, scenePoint, null);

          if (rootHit) {
            const deepNode = state.doc.nodes[rootHit.hitId];

            // Walk up from the deepest hit node to find the direct child of
            // the currently selected group -- that tells us how deep to drill.
            // Build chain: [selectedGroup, child, grandchild, ..., hitNode]
            const chain: string[] = [];
            let cur = rootHit.hitId;
            while (cur && cur !== targetNodeId) {
              chain.unshift(cur);
              const n = state.doc.nodes[cur];
              cur = n?.parentId ?? '';
            }

            if (chain.length > 0) {
              // Enter the deepest GROUP/FRAME ancestor of the hit node
              // (or the hit node's parent if the hit is a leaf)
              let enterTarget = targetNodeId; // default: enter the selected group
              for (const id of chain) {
                const n = state.doc.nodes[id];
                if (n && (n.type === 'GROUP' || n.type === 'FRAME')) {
                  enterTarget = id;
                } else {
                  break; // stop at first non-group
                }
              }

              state.enterGroup(enterTarget);

              // If the deepest hit is TEXT and it's a direct child of the
              // entered group, start editing immediately
              if (deepNode?.type === 'TEXT' && deepNode.parentId === enterTarget) {
                state.select(rootHit.hitId);
                state.startTextEdit(rootHit.hitId);
              } else {
                // Select the appropriate child within the entered group
                const innerHit = hitTest(state.doc, scenePoint, enterTarget);
                if (innerHit) {
                  state.select(innerHit.selectableId);
                  // If the selected child is TEXT, edit it
                  const selNode = state.doc.nodes[innerHit.selectableId];
                  if (selNode?.type === 'TEXT') {
                    state.startTextEdit(innerHit.selectableId);
                  }
                }
              }
            }
          } else {
            // No hit found, just enter the group
            state.enterGroup(targetNodeId);
          }
        }
      }
    },
    [store, screenToScene, options],
  );

  // Wheel must use native listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (options?.lockViewport) {
        // Let the event propagate to the outer EmailCanvasViewport for zoom/pan
        return;
      }
      e.preventDefault();
      const state = store.getState();

      // Scroll-to-zoom inside crop mode: scales the image around the cursor
      // (not the canvas viewport). Holding ctrl/meta still falls through to
      // canvas zoom so the user can zoom the document while cropping.
      if (state.croppingNodeId && !e.ctrlKey && !e.metaKey) {
        const canvasX = e.clientX - el.getBoundingClientRect().left;
        const canvasY = e.clientY - el.getBoundingClientRect().top;
        const sceneX = (canvasX - state.panOffset.x) / state.zoom;
        const sceneY = (canvasY - state.panOffset.y) / state.zoom;
        const factor = Math.exp(-e.deltaY * 0.0015);
        state.zoomCroppedImage({ x: sceneX, y: sceneY }, factor);
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        const delta = -e.deltaY * 0.002;
        const newZoom = state.zoom * (1 + delta);
        const rect = el.getBoundingClientRect();
        state.setZoom(newZoom, {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      } else {
        store.setState({
          panOffset: {
            x: state.panOffset.x - e.deltaX,
            y: state.panOffset.y - e.deltaY,
          },
          renderVersion: state.renderVersion + 1,
        });
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [store, canvasRef]);

  // ── Keyboard events ──────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const state = store.getState();

      if (e.key === ' ') {
        e.preventDefault();
        spaceHeldRef.current = true;
        if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
        return;
      }

      // Alt held with a live selection turns on the measurement overlay (the
      // renderer draws gap lines from the selection box to the hovered node).
      // Purely a hover affordance: skipped mid-drag and while editing text, and
      // not returned so Alt still composes with drag-duplicate / resize-center.
      if (e.key === 'Alt' && !mouseDownRef.current && !state.altMeasureActive) {
        if (state.selectedIds.size > 0 && state.interactionMode === 'idle' && !state.editingTextId) {
          store.setState({ altMeasureActive: true, renderVersion: state.renderVersion + 1 });
        }
      }

      if (e.key === 'Enter' && state.drawTool === 'PEN' && state.penCurrentPath) {
        e.preventDefault();
        state.penToolFinish();
        return;
      }

      // Enter descends into the selected container (first child); Shift+Enter
      // ascends to the parent. Text editing is handled by the overlay (the
      // guard above returns while a contentEditable is focused), and the pen's
      // open path already claimed Enter above.
      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !state.editingTextId && !state.drawTool) {
        if (state.selectedIds.size === 1) {
          e.preventDefault();
          if (e.shiftKey) state.ascendSelection();
          else state.descendSelection();
        }
        return;
      }

      // Tab / Shift+Tab select the next / previous sibling of the current
      // selection. Only claim Tab when it will act, so normal focus navigation
      // still works when nothing is selected.
      if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (state.selectedIds.size === 1) {
          e.preventDefault();
          state.selectSibling(e.shiftKey ? -1 : 1);
        }
        return;
      }

      if (e.key === 'Escape') {
        if (state.drawTool === 'PEN' && state.penCurrentPath) {
          state.penToolFinish();
          return;
        }
        if (state.interactionMode === 'drawing') {
          state.cancelDrawing();
          return;
        }
        if (state.drawTool) {
          state.setDrawTool(null);
          return;
        }
        if (state.vectorEditingNodeId) {
          state.exitVectorEditMode();
          return;
        }
        if (state.croppingNodeId) {
          state.exitCropMode();
          return;
        }
        if (state.editingTextId) {
          state.cancelTextEdit();
          return;
        }
        state.enteredGroupId ? state.exitGroup() : state.clearSelection();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (state.vectorEditingNodeId && state.vectorSelectedPoints.size > 0) {
          state.deleteVectorPoints();
          return;
        }
        state.removeSelected();
        return;
      }

      const isCmd = e.metaKey || e.ctrlKey;

      if (isCmd && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        state.undo();
        return;
      }
      if ((isCmd && e.key === 'z' && e.shiftKey) || (isCmd && e.key === 'y')) {
        e.preventDefault();
        state.redo();
        return;
      }

      // Distribute spacing (Figma parity): Ctrl+Opt+H / Ctrl+Opt+V. Ctrl
      // specifically (not Cmd) so on macOS it stays distinct from Cmd+Opt+V
      // paste-style below; on Windows the combos collide, so distribute only
      // claims the key at 3+ selected nodes (distributeSelected's own floor)
      // and otherwise falls through to paste-style, resolving by context the
      // way Figma itself does.
      if (e.ctrlKey && !e.metaKey && e.altKey && !e.shiftKey && (e.code === 'KeyH' || e.code === 'KeyV')) {
        if (state.selectedIds.size >= 3) {
          e.preventDefault();
          state.distributeSelected(e.code === 'KeyH' ? 'horizontal' : 'vertical');
          return;
        }
      }

      // Copy/paste style (Figma parity): Cmd/Ctrl+Opt+C snapshots one node's
      // visual + text style; Cmd/Ctrl+Opt+V applies it to every selected node
      // as one undo step. Match on e.code — Option rewrites e.key to a glyph on
      // macOS — and run before plain copy/paste so the modifier combo wins.
      if (isCmd && e.altKey && (e.code === 'KeyC' || e.code === 'KeyV')) {
        if (e.code === 'KeyC') {
          if (state.selectedIds.size === 1) {
            e.preventDefault();
            const node = state.doc.nodes[[...state.selectedIds][0]];
            if (node) {
              styleClipboard = snapshotNodeStyle(node);
              toast.success('Style copied');
            }
          }
        } else if (styleClipboard && state.selectedIds.size > 0) {
          e.preventDefault();
          const snap = styleClipboard;
          const patches = [...state.selectedIds]
            .map((id) => state.doc.nodes[id])
            .filter((n): n is SceneNode => !!n)
            .map((n) => ({ nodeId: n.id, props: styleForTarget(snap, n) }));
          if (patches.length > 0) {
            state.applyNodePatches(patches);
            toast.success(patches.length > 1 ? 'Style pasted to selection' : 'Style pasted');
          }
        }
        return;
      }

      if (isCmd && !e.altKey && e.key === 'c' && !e.shiftKey) {
        e.preventDefault();
        state.copySelected();
        writeSystemClipboard(store);
        return;
      }
      // Cmd+V is deliberately NOT handled here: preventDefault on the keydown
      // would suppress the native `paste` event, and that event is the only way
      // to read the system clipboard without a permission prompt. See the paste
      // listener below.
      //
      // Cut (Figma parity): copy to the clipboard, then delete, so a later
      // paste re-places the nodes. The delete is the single undo step (copy
      // never touches history). Falls through when nothing is selected.
      if (isCmd && !e.altKey && !e.shiftKey && e.key === 'x') {
        if (state.selectedIds.size > 0) {
          e.preventDefault();
          state.copySelected();
          writeSystemClipboard(store);
          state.removeSelected();
          return;
        }
      }
      if (isCmd && e.key === 'd') {
        e.preventDefault();
        state.duplicateSelected();
        return;
      }
      if (isCmd && e.key === 'a') {
        e.preventDefault();
        state.selectAll();
        return;
      }

      // Rename (Figma parity): Cmd/Ctrl+R asks the selected node's Layers-panel
      // row to open its inline rename (the row claims the event by cancelling
      // it). Claimed for any selection so the combo never falls through to the
      // browser's reload while working; with nothing selected it still reloads.
      if (isCmd && !e.altKey && !e.shiftKey && e.key === 'r') {
        if (state.selectedIds.size > 0) {
          e.preventDefault();
          if (state.selectedIds.size > 1) {
            toast.info('Select a single layer to rename');
            return;
          }
          const nodeId = [...state.selectedIds][0];
          const claimed = !window.dispatchEvent(
            new CustomEvent('nexmail:renameLayer', { detail: { nodeId }, cancelable: true }),
          );
          if (!claimed) toast.info('Open the Layers panel to rename this layer');
          return;
        }
      }

      // Flatten (Figma parity, Cmd/Ctrl+E): merge the two selected shapes into
      // one PATH via the boolean-union pipeline (booleanOp's exactly-2 floor).
      // Shift+Cmd+O outlines a selected TEXT node into vector paths — Figma's
      // outline-stroke behavior for text (stroke-to-path for shapes doesn't
      // exist yet). Both dispatch the events the properties panel already
      // uses, since CanvasKitEditor owns the CanvasKit instance the geometry
      // needs. Unclaimed when the selection doesn't fit, so the browser combo
      // still works.
      if (isCmd && !e.altKey && !e.shiftKey && e.key === 'e') {
        if (state.selectedIds.size === 2) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('nexmail:booleanOp', { detail: { op: 'union' } }));
          return;
        }
      }
      if (isCmd && e.shiftKey && !e.altKey && e.code === 'KeyO') {
        const ids = [...state.selectedIds];
        if (ids.length === 1 && state.doc.nodes[ids[0]]?.type === 'TEXT') {
          e.preventDefault();
          window.dispatchEvent(
            new CustomEvent('nexmail:textToOutlines', { detail: { nodeId: ids[0] } }),
          );
          return;
        }
      }

      // Cmd/Ctrl+Opt+G wraps the selection in a new frame. Match on e.code —
      // Option rewrites e.key to a glyph on macOS — and take priority over the
      // plain group combo below.
      if (isCmd && e.altKey && e.code === 'KeyG') {
        e.preventDefault();
        state.frameSelected();
        return;
      }
      if (isCmd && !e.altKey && e.key === 'g' && !e.shiftKey) {
        e.preventDefault();
        state.groupSelected();
        return;
      }
      if (isCmd && !e.altKey && e.key === 'g' && e.shiftKey) {
        e.preventDefault();
        state.ungroupSelected();
        return;
      }

      // Z-order (Figma parity): Cmd+] forward, Cmd+[ backward, add Opt for
      // front/back. Match on e.code so Option-rewritten glyphs don't break it.
      if (isCmd && (e.code === 'BracketRight' || e.code === 'BracketLeft')) {
        e.preventDefault();
        const toEnd = e.altKey;
        if (e.code === 'BracketRight') {
          state.moveZOrder(toEnd ? 'toFront' : 'forward');
        } else {
          state.moveZOrder(toEnd ? 'toBack' : 'backward');
        }
        return;
      }

      // Lock (Cmd+Shift+L) / hide (Cmd+Shift+H) the selection. Mixed states
      // collapse to one consistent value, like the bold toggle. preventDefault
      // beats the browser's own Cmd+Shift+L/H bindings.
      if (isCmd && e.shiftKey && (e.code === 'KeyL' || e.code === 'KeyH')) {
        const ids = [...state.selectedIds].filter((id) => state.doc.nodes[id]);
        if (ids.length > 0) {
          e.preventDefault();
          if (e.code === 'KeyL') {
            const anyLocked = ids.some((id) => state.doc.nodes[id]?.locked);
            state.applyNodePatches(ids.map((id) => ({ nodeId: id, props: { locked: !anyLocked } })));
          } else {
            const anyVisible = ids.some((id) => state.doc.nodes[id]?.visible !== false);
            state.applyNodePatches(
              ids.map((id) => ({ nodeId: id, props: { visible: !anyVisible } })),
            );
          }
          return;
        }
      }

      // Auto layout (Figma parity): Shift+A adds auto-layout to the selected
      // frame(s); Opt/Alt+Shift+A removes it (back to free positioning). Match
      // on e.code — Option rewrites e.key to a glyph on macOS — and require a
      // selection so the combo falls through when nothing is selected.
      if (!isCmd && e.shiftKey && e.code === 'KeyA' && state.selectedIds.size > 0) {
        e.preventDefault();
        const hasFrame = [...state.selectedIds].some((id) => {
          const ty = state.doc.nodes[id]?.type;
          return ty === 'FRAME' || ty === 'GROUP';
        });
        if (!hasFrame) {
          toast.info('Select a section or frame to toggle its auto-layout');
          return;
        }
        const on = !e.altKey;
        state.setSelectedAutoLayout(on);
        toast.success(on ? 'Auto-layout on' : 'Auto-layout off — drag freely');
        return;
      }

      // Float / "ignore auto-layout" (Figma's Absolute position): Shift+F pops
      // the selected element out of its auto-layout flow so it free-drags
      // (its frame is pinned so nothing collapses), or docks it back in.
      if (!isCmd && e.shiftKey && e.code === 'KeyF' && state.selectedIds.size === 1) {
        const id = [...state.selectedIds][0];
        const node = state.doc.nodes[id];
        if (node?.parentId) {
          e.preventDefault();
          const wasFloating = node.layoutPositioning === 'ABSOLUTE';
          state.toggleSelectedFloating();
          toast.success(wasFloating ? 'Docked back into layout' : 'Floated — drag it freely');
          return;
        }
      }

      // Show / hide all layout guides (Figma's grid toggle; Cmd+G is Group, so
      // Shift+G here). Match on e.code so the shifted glyph doesn't matter.
      if (!isCmd && e.shiftKey && !e.altKey && e.code === 'KeyG') {
        e.preventDefault();
        state.toggleGuidesVisible();
        toast.success(state.guidesVisible ? 'Guides hidden' : 'Guides shown');
        return;
      }

      // Align (Figma parity): Opt+A/D/W/S = left/right/top/bottom, Opt+H/V =
      // horizontal/vertical centers. Match on e.code — Option rewrites e.key
      // to a glyph on macOS — and require 2+ nodes (alignSelected's own floor)
      // so bare Opt combos fall through otherwise.
      if (!isCmd && e.altKey && !e.shiftKey) {
        const edgeByCode: Record<string, AlignEdge> = {
          KeyA: 'left',
          KeyD: 'right',
          KeyW: 'top',
          KeyS: 'bottom',
          KeyH: 'hcenter',
          KeyV: 'vcenter',
        };
        const edge = edgeByCode[e.code];
        if (edge && state.selectedIds.size >= 2) {
          e.preventDefault();
          state.alignSelected(edge);
          return;
        }
      }

      // Panel keys: Opt+1..9 select sidebar tabs in icon-bar order, top to
      // bottom (hosts list Layers first, so Opt+1 = Layers, matching Figma);
      // Opt+P / Opt+C switch the right panel to Properties / chat. Dispatched
      // as events because the panels are host-owned component state, like the
      // rename request.
      if (!isCmd && e.altKey && !e.shiftKey && /^Digit[1-9]$/.test(e.code)) {
        e.preventDefault();
        const slot = Number(e.code.slice(5));
        window.dispatchEvent(new CustomEvent('nexmail:sidebarTab', { detail: { slot } }));
        return;
      }
      if (!isCmd && e.altKey && !e.shiftKey && (e.code === 'KeyP' || e.code === 'KeyC')) {
        e.preventDefault();
        window.dispatchEvent(
          new CustomEvent('nexmail:rightPanel', {
            detail: { panel: e.code === 'KeyP' ? 'properties' : 'chat' },
          }),
        );
        return;
      }

      // Text alignment (Figma parity): Cmd/Ctrl+Opt+L/T/R/J = left/center/right/justify.
      // macOS rewrites e.key to a glyph when Option is held, so match on e.code instead.
      // Only act when one or more TEXT nodes are selected; otherwise let the combo
      // fall through to the browser (e.g. Cmd+Opt+J opening the dev console).
      if (isCmd && e.altKey) {
        const alignByCode: Record<string, 'left' | 'center' | 'right' | 'justify'> = {
          KeyL: 'left',
          KeyT: 'center',
          KeyR: 'right',
          KeyJ: 'justify',
        };
        const align = alignByCode[e.code];
        if (align) {
          const textIds = [...state.selectedIds].filter(
            (id) => state.doc.nodes[id]?.type === 'TEXT',
          );
          if (textIds.length > 0) {
            e.preventDefault();
            state.applyNodePatches(
              textIds.map((id) => ({ nodeId: id, props: { textAlign: align } })),
            );
            return;
          }
        }
      }

      // Tidy up (Figma parity): Ctrl+Opt+T. Sits after the text-align block so
      // the combo on a text selection keeps meaning align-center — Figma
      // overloads the same physical keys on Windows and resolves by context
      // the same way. 2+ nodes is tidyUpSelected's own floor.
      if (e.ctrlKey && !e.metaKey && e.altKey && !e.shiftKey && e.code === 'KeyT') {
        if (state.selectedIds.size >= 2) {
          e.preventDefault();
          state.tidyUpSelected();
          return;
        }
      }

      // Text size (Figma parity): Cmd/Ctrl+Shift+'>'/'<' grows/shrinks font
      // size, by 1pt normally or 10pt with Opt/Alt held. e.code is layout-
      // stable (Shift+Period yields '>' but the code stays 'Period').
      if (isCmd && e.shiftKey && (e.code === 'Period' || e.code === 'Comma')) {
        const textIds = [...state.selectedIds].filter(
          (id) => state.doc.nodes[id]?.type === 'TEXT',
        );
        if (textIds.length > 0) {
          e.preventDefault();
          const step = e.altKey ? 10 : 1;
          const delta = e.code === 'Period' ? step : -step;
          state.applyNodePatches(
            textIds.map((id) => {
              const cur = state.doc.nodes[id]?.fontSize ?? 16;
              return { nodeId: id, props: { fontSize: Math.max(1, Math.round(cur + delta)) } };
            }),
          );
          return;
        }
      }

      // Font weight (Figma parity): Cmd/Ctrl+Opt+'>'/'<' steps the weight
      // through the hundreds, clamped to 100-900. The font-sync effect fetches
      // the real face for the new weight where the family provides one.
      if (isCmd && e.altKey && !e.shiftKey && (e.code === 'Period' || e.code === 'Comma')) {
        const textIds = [...state.selectedIds].filter(
          (id) => state.doc.nodes[id]?.type === 'TEXT',
        );
        if (textIds.length > 0) {
          e.preventDefault();
          const delta = e.code === 'Period' ? 100 : -100;
          state.applyNodePatches(
            textIds.map((id) => {
              const cur = state.doc.nodes[id]?.fontWeight ?? 400;
              const next = Math.max(100, Math.min(900, Math.round(cur / 100) * 100 + delta));
              return { nodeId: id, props: { fontWeight: next } };
            }),
          );
          return;
        }
      }

      // Letter spacing (Opt+'<'/'>') and line height (Shift+Opt+'<'/'>'),
      // Figma parity. letterSpacing is px, stepped by 0.5 (negatives allowed);
      // lineHeight is the unitless multiplier, stepped by 0.1 with a 0.5 floor.
      if (!isCmd && e.altKey && (e.code === 'Period' || e.code === 'Comma')) {
        const textIds = [...state.selectedIds].filter(
          (id) => state.doc.nodes[id]?.type === 'TEXT',
        );
        if (textIds.length > 0) {
          e.preventDefault();
          const dir = e.code === 'Period' ? 1 : -1;
          state.applyNodePatches(
            textIds.map((id) => {
              const node = state.doc.nodes[id];
              if (e.shiftKey) {
                const next = Math.max(0.5, Math.round(((node?.lineHeight ?? 1.2) + 0.1 * dir) * 100) / 100);
                return { nodeId: id, props: { lineHeight: next } };
              }
              const next = Math.round(((node?.letterSpacing ?? 0) + 0.5 * dir) * 10) / 10;
              return { nodeId: id, props: { letterSpacing: next } };
            }),
          );
          return;
        }
      }

      // Text style toggles (Figma parity): Cmd/Ctrl+B bold (400 <-> 700),
      // Cmd/Ctrl+I italic. The real face is fetched on demand by the editor's
      // font-sync effect, so toggling renders a true face (not a synthesized
      // slant) wherever the family is fetchable. Act on every selected TEXT
      // node; no-op otherwise so the combo falls through.
      if (isCmd && !e.altKey && !e.shiftKey && (e.key === 'b' || e.key === 'i')) {
        const textIds = [...state.selectedIds].filter(
          (id) => state.doc.nodes[id]?.type === 'TEXT',
        );
        if (textIds.length > 0) {
          e.preventDefault();
          if (e.key === 'b') {
            // Toggle off if any selected node is already bold, so a mixed
            // selection collapses to a single consistent state.
            const anyBold = textIds.some((id) => (state.doc.nodes[id]?.fontWeight ?? 400) >= 600);
            const nextWeight = anyBold ? 400 : 700;
            state.applyNodePatches(
              textIds.map((id) => ({ nodeId: id, props: { fontWeight: nextWeight } })),
            );
          } else {
            const anyItalic = textIds.some((id) => state.doc.nodes[id]?.fontStyle === 'italic');
            const nextStyle = anyItalic ? 'normal' : 'italic';
            state.applyNodePatches(
              textIds.map((id) => ({ nodeId: id, props: { fontStyle: nextStyle } })),
            );
          }
          return;
        }
      }

      // Underline (Cmd/Ctrl+U) and strikethrough (Cmd/Ctrl+Shift+X), Figma
      // parity: toggle the node-level decoration; a mixed selection collapses
      // to one consistent state like the bold toggle.
      if (isCmd && !e.altKey && ((!e.shiftKey && e.key === 'u') || (e.shiftKey && e.code === 'KeyX'))) {
        const textIds = [...state.selectedIds].filter(
          (id) => state.doc.nodes[id]?.type === 'TEXT',
        );
        if (textIds.length > 0) {
          e.preventDefault();
          const deco = e.shiftKey ? 'line-through' : 'underline';
          const anyOn = textIds.some((id) => state.doc.nodes[id]?.textDecoration === deco);
          state.applyNodePatches(
            textIds.map((id) => ({ nodeId: id, props: { textDecoration: anyOn ? 'none' : deco } })),
          );
          return;
        }
      }

      // Zoom keys (Figma parity): Cmd+= in, Cmd+- out (~1.25x about the
      // viewport center), Cmd+0 / Shift+0 reset to 100%. preventDefault beats
      // the browser's own zoom.
      if (isCmd && (e.code === 'Equal' || e.code === 'Minus')) {
        e.preventDefault();
        const rect = canvasRef.current?.getBoundingClientRect();
        const center = rect ? { x: rect.width / 2, y: rect.height / 2 } : undefined;
        const factor = e.code === 'Equal' ? 1.25 : 1 / 1.25;
        state.setZoom(state.zoom * factor, center);
        return;
      }
      if ((isCmd || e.shiftKey) && e.code === 'Digit0') {
        e.preventDefault();
        const rect = canvasRef.current?.getBoundingClientRect();
        const center = rect ? { x: rect.width / 2, y: rect.height / 2 } : undefined;
        state.setZoom(1, center);
        return;
      }

      // Shift+1 = zoom to fit document, Shift+2 = zoom to selection (Figma parity).
      // Use e.code so it works regardless of the shifted character ('!' / '@').
      if (e.shiftKey && (e.code === 'Digit1' || e.code === 'Digit2')) {
        e.preventDefault();
        const rect = canvasRef.current?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0) {
          if (e.code === 'Digit1') state.zoomToFit(rect.width, rect.height);
          else state.zoomToSelection(rect.width, rect.height);
        }
        return;
      }

      // Single-key tools (Figma parity): V select, R rectangle, O ellipse,
      // L line, P pen, Shift+P pencil (freehand), T drops a text block at the
      // artboard center. Bare keys only, and only while idle — crop, vector
      // edit, and text edit keep their keys (Escape already exits those), and
      // the contentEditable focus guard at the top covers live typing.
      const toolIdle =
        !isCmd && !e.altKey &&
        !state.editingTextId && !state.croppingNodeId && !state.vectorEditingNodeId;
      if (toolIdle && e.shiftKey && e.code === 'KeyP') {
        e.preventDefault();
        state.setDrawTool('FREEHAND');
        return;
      }
      if (toolIdle && !e.shiftKey) {
        const toolByCode: Record<string, DrawToolType | null> = {
          KeyV: null,
          KeyR: 'RECTANGLE',
          KeyO: 'ELLIPSE',
          KeyL: 'LINE',
          KeyP: 'PEN',
        };
        if (e.code in toolByCode) {
          e.preventDefault();
          state.setDrawTool(toolByCode[e.code]);
          return;
        }
        if (e.code === 'KeyT') {
          e.preventDefault();
          state.addNewNode({
            id: generateNodeId(),
            type: 'TEXT',
            parentId: null,
            x: state.doc.width / 2 - 75,
            y: state.doc.height / 2 - 12,
            width: 150,
            height: 24,
            text: 'Text',
            fontSize: 16,
            fontFamily: 'Roboto',
            fontWeight: 400,
            textAlign: 'left',
            verticalAlign: 'top',
            fills: [{ type: 'SOLID', color: '#000000' }],
          });
          return;
        }
      }

      // Fill & stroke keys (Figma parity): '/' removes the stroke, Opt+'/'
      // removes the fill, Shift+X swaps fill and stroke (a node gaining a
      // stroke without a weight gets 1px so the swap is visible). Same idle
      // guards as the tool keys; only nodes that actually change are patched
      // so an empty pass never pushes history.
      const shapeIdle =
        !isCmd && !state.editingTextId && !state.croppingNodeId && !state.vectorEditingNodeId;
      if (shapeIdle && !e.shiftKey && e.code === 'Slash' && state.selectedIds.size > 0) {
        e.preventDefault();
        const removeFill = e.altKey;
        const patches = [...state.selectedIds]
          .map((id) => state.doc.nodes[id])
          .filter((n): n is SceneNode =>
            removeFill ? (n?.fills?.length ?? 0) > 0 : (n?.strokes?.length ?? 0) > 0,
          )
          .map((n) => ({ nodeId: n.id, props: removeFill ? { fills: [] } : { strokes: [] } }));
        if (patches.length > 0) state.applyNodePatches(patches);
        return;
      }
      if (shapeIdle && e.shiftKey && !e.altKey && e.code === 'KeyX' && state.selectedIds.size > 0) {
        e.preventDefault();
        const patches = [...state.selectedIds]
          .map((id) => state.doc.nodes[id])
          .filter(
            (n): n is SceneNode =>
              !!n && ((n.fills?.length ?? 0) > 0 || (n.strokes?.length ?? 0) > 0),
          )
          .map((n) => {
            const props: Partial<SceneNode> = { fills: n.strokes ?? [], strokes: n.fills ?? [] };
            if ((n.fills?.length ?? 0) > 0 && !(n.strokeWeight && n.strokeWeight > 0)) {
              props.strokeWeight = 1;
            }
            return { nodeId: n.id, props };
          });
        if (patches.length > 0) state.applyNodePatches(patches);
        return;
      }

      // Opacity number keys (Figma parity): a bare digit sets the selection's
      // opacity (5 = 50%, 0 = 100%); a second digit typed within the compose
      // window builds a two-digit value (5 then 5 = 55%). Skipped while drawing
      // or editing text, and only with a selection.
      if (
        !isCmd && !e.altKey && !e.shiftKey &&
        e.key >= '0' && e.key <= '9' &&
        !state.drawTool && !state.editingTextId && !state.vectorEditingNodeId &&
        state.selectedIds.size > 0
      ) {
        e.preventDefault();
        const now = Date.now();
        const prev = opacityTypingRef.current;
        let pct: number;
        if (prev && now - prev.at < 500) {
          pct = parseInt(prev.digit + e.key, 10); // e.g. '5' then '5' -> 55
          opacityTypingRef.current = null;
        } else {
          pct = e.key === '0' ? 100 : Number(e.key) * 10;
          opacityTypingRef.current = { digit: e.key, at: now };
        }
        const opacity = Math.max(0, Math.min(1, pct / 100));
        const ids = [...state.selectedIds].filter((id) => state.doc.nodes[id]);
        if (ids.length > 0) {
          state.applyNodePatches(ids.map((id) => ({ nodeId: id, props: { opacity } })));
        }
        return;
      }

      const nudge = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowLeft') { e.preventDefault(); state.nudge(-nudge, 0); }
      if (e.key === 'ArrowRight') { e.preventDefault(); state.nudge(nudge, 0); }
      if (e.key === 'ArrowUp') { e.preventDefault(); state.nudge(0, -nudge); }
      if (e.key === 'ArrowDown') { e.preventDefault(); state.nudge(0, nudge); }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        spaceHeldRef.current = false;
        if (canvasRef.current) canvasRef.current.style.cursor = 'default';
      }
      if (e.key === 'Alt') {
        const st = store.getState();
        if (st.altMeasureActive) {
          store.setState({ altMeasureActive: false, renderVersion: st.renderVersion + 1 });
        }
      }
    };

    // Alt-up can be missed if focus leaves the window while it's held; clear
    // the measurement overlay (and the pan cursor) on blur so it never sticks.
    const handleBlur = () => {
      spaceHeldRef.current = false;
      const st = store.getState();
      if (st.altMeasureActive) {
        store.setState({ altMeasureActive: false, renderVersion: st.renderVersion + 1 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [store, canvasRef, screenToScene]);

  // ── Paste ────────────────────────────────────────────────────────
  //
  // Cmd+V arrives here rather than in handleKeyDown: the native `paste` event
  // is the one place the system clipboard can be read without prompting for
  // permission, so nothing upstream may preventDefault the keydown.

  const onOsFilesDropped = options?.onOsFilesDropped;

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // React attaches its listeners at the root container, so a synthetic
      // stopPropagation in the text-edit overlay doesn't stop the native event
      // reaching window. Bail on editable targets the way handleKeyDown does,
      // so the overlay and every form field keep their own paste behavior.
      const target = e.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) {
        return;
      }

      const state = store.getState();
      const data = e.clipboardData;

      // Paste at the cursor when the pointer is over the canvas; otherwise let
      // the store fall back to its default +10/+10 offset.
      const rect = canvasRef.current?.getBoundingClientRect();
      const pc = pointerClientRef.current;
      const overCanvas =
        !!rect && !!pc &&
        pc.x >= rect.left && pc.x <= rect.right &&
        pc.y >= rect.top && pc.y <= rect.bottom;
      const at = overCanvas ? screenToScene(pc!.x, pc!.y) : undefined;

      const payload = parseCanvasClipboard(data?.getData('text/plain'));
      if (payload) {
        e.preventDefault();
        if (payload.stamp === state.clipboardStamp && state.clipboardNodes) {
          // This editor's own copy, still in memory: paste the raw nodes so
          // brand tokens stay linked and re-theme with the brand.
          state.pasteClipboard(at);
        } else {
          // From another document. Colors were baked against the source brand
          // at copy time, so this reproduces what was copied.
          state.pasteExternalNodes(payload.nodes, payload.topIds, payload.stamp, at);
        }
        return;
      }

      // A screenshot or copied image file: route through the same upload +
      // context-aware placement the OS file drop uses, so a paste over a shape
      // fills it and a paste on empty canvas places a node.
      const files = readClipboardImageFiles(data);
      if (files.length > 0 && onOsFilesDropped) {
        e.preventDefault();
        const atScene = at ?? { x: state.doc.width / 2, y: state.doc.height / 2 };
        const hit = hitTest(state.doc, atScene, state.enteredGroupId, state.linkZonesVisible);
        onOsFilesDropped(files, { atScene, targetNodeId: hit?.selectableId });
        return;
      }

      // Nothing of ours on the system clipboard. Fall back to the in-editor
      // clipboard so a copy still pastes when the clipboard write was refused.
      if (state.clipboardNodes && state.clipboardTopIds.length > 0) {
        e.preventDefault();
        state.pasteClipboard(at);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [store, canvasRef, screenToScene, onOsFilesDropped]);

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
  };
}
